/**
 * The chat of the game (port addition: the original had none): the main map's chat button with its unread badge and
 * the chat panel (src/game/ui/chat-panel.ts) over the main map, on the backend's chat (protocol chat.*;
 * src/server/core/services/chat.ts). This controller is the part that outlives the panel:
 *  - whether the game shows the chat: the backend offers it (GameServices.chatAvailable) and a player is signed in;
 *  - the unread counts of the badge: chat.channels while the main map is shown and the panel is closed, at most every
 *    POLL_MS and once when the main map comes back (no event stream while the panel is closed: a closed chat costs
 *    the server nothing but this poll);
 *  - the players this device hides (a player preference, CHAT_HIDDEN_PREF, wiped at a sign-out; never sent anywhere
 *    but as chat.channels `exclude`, so that their messages do not light the badge): their messages are not shown.
 * The panel subscribes to the channels while it is open and unsubscribes when it closes (chat.subscribe /
 * chat.unsubscribe), so live messages reach only players who look at the chat.
 */
/**
 * A chat message as the game reads it (ChatMessage of the protocol, read structurally: the game never imports the
 * protocol).
 */
export interface ChatLine {
  messageId: string;
  channel: string;
  seq: number;
  at: number;
  playerId: string;
  nickname: string;
  level: number;
  clan: { clanId: number; name: string; emblem: number } | null;
  text: string;
}

/** A readable channel as chat.channels tells it (ChatChannelInfo of the protocol, read structurally). */
export interface ChatChannelInfo {
  channel: string;
  kind: 'global' | 'clan';
  clan: { clanId: number; name: string; emblem: number } | null;
  canSend: boolean;
  sendBlock: 'guest' | 'level' | 'muted' | null;
  lastSeq: number;
  readSeq: number;
  unread: number;
  subscribed: boolean;
}

/** The player preference with the hidden players: [{ id, name }], newest last. */
export const CHAT_HIDDEN_PREF = 'chat_hidden';
/** The most players one device hides. */
export const CHAT_HIDDEN_MAX = 200;
/** How often the badge asks for the unread counts while the main map is shown. */
export const CHAT_POLL_MS = 30_000;

export interface HiddenPlayer {
  id: string;
  name: string;
}

export class ChatController {
  /** The readable channels as the server last told them (chat.channels). */
  channels: ChatChannelInfo[] = [];
  mutedUntil: number | null = null;
  /** The chat panel while it is open. */
  panel: any = null;
  /**
   * The unsubscription of the panel that closed last, while it is under way: a panel opened right after waits for it
   * before it subscribes, so the late unsubscription never ends the new subscription.
   */
  unsubscribing: Promise<unknown> | null = null;
  private lastPoll = Number.NEGATIVE_INFINITY;
  private polling = false;
  /** The request generation (poll, reset): only the answer of the latest request is adopted. */
  private pollGen = 0;
  /** Requests answered (tests). */
  polls = 0;

  constructor(
    private readonly fwp: any,
    private readonly now: () => number = () => Date.now(),
  ) {}

  private get services(): any {
    return this.fwp && this.fwp.services;
  }

  /** The backend offers the chat and a player is signed in. */
  available(): boolean {
    const s = this.services;
    return !!s && typeof s.chatAvailable === 'function' && typeof s.chatChannels === 'function' && s.chatAvailable() && !!this.fwp.user && !!this.fwp.user.player_id;
  }

  /** The unread messages of every channel (the badge). */
  get unread(): number {
    return this.channels.reduce((n, c) => n + (Number(c.unread) || 0), 0);
  }

  // ------------------------------------------------------------------------------------------------ hidden players

  hidden(): HiddenPlayer[] {
    const v = this.fwp.pref(CHAT_HIDDEN_PREF);
    if (!Array.isArray(v)) return [];
    return v.filter((h: any) => h && typeof h.id === 'string' && typeof h.name === 'string').slice(-CHAT_HIDDEN_MAX);
  }

  isHidden(playerId: string): boolean {
    return this.hidden().some((h) => h.id === playerId);
  }

  hide(playerId: string, name: string): void {
    const list = this.hidden().filter((h) => h.id !== playerId);
    list.push({ id: playerId, name });
    this.fwp.pref(CHAT_HIDDEN_PREF, list.slice(-CHAT_HIDDEN_MAX));
    this.poll(true);
  }

  unhide(playerId: string): void {
    this.fwp.pref(
      CHAT_HIDDEN_PREF,
      this.hidden().filter((h) => h.id !== playerId),
    );
    this.poll(true);
  }

  // ------------------------------------------------------------------------------------------------ the badge

  /** Called every frame while the main map's chat button is shown: asks for the counts when they are due. */
  tick(): void {
    if (this.panel || !this.available()) return;
    if (this.now() - this.lastPoll >= CHAT_POLL_MS) this.poll();
  }

  /** The counts again at the next tick (the main map came back, the panel closed). */
  refreshSoon(): void {
    this.lastPoll = Number.NEGATIVE_INFINITY;
  }

  /**
   * chat.channels now (`force`: even while a request is under way, e.g. after hiding a player). Only the latest
   * request's answer counts: an older one answered after it (it still counted the player just hidden), or one answered
   * after reset() (another account's counts), is dropped.
   */
  poll(force = false): void {
    if (!this.available() || (this.polling && !force)) return;
    const gen = ++this.pollGen;
    this.polling = true;
    this.lastPoll = this.now();
    Promise.resolve(this.services.chatChannels(this.hidden().map((h) => h.id))).then(
      (r: { channels: ChatChannelInfo[]; mutedUntil: number | null }) => {
        if (gen !== this.pollGen) return;
        this.polling = false;
        this.polls++;
        this.adopt(r);
      },
      () => {
        // (no connection, a session that ended: the next poll tries again; the game handles the session itself)
        if (gen === this.pollGen) this.polling = false;
      },
    );
  }

  /** The server's answer of chat.channels (also from the open panel). */
  adopt(r: { channels: ChatChannelInfo[]; mutedUntil: number | null } | null | undefined): void {
    if (!r || !Array.isArray(r.channels)) return;
    this.channels = r.channels;
    this.mutedUntil = r.mutedUntil ?? null;
  }

  /** The panel closed or the player was forgotten: the badge starts again from the server's counts. */
  reset(): void {
    // (a request under way answers for the previous account: dropped)
    this.pollGen++;
    this.polling = false;
    this.channels = [];
    this.mutedUntil = null;
    this.refreshSoon();
  }
}
